/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useEffect, useCallback } from "react";
import { ShieldCheckIcon, LockClosedIcon, ClockIcon } from "@heroicons/react/24/outline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth";
import {
  setupTotp,
  confirmTotp,
  disableTotp,
  getLoginHistory,
  updateAccount,
  updatePassword,
  LoginAttempt,
} from "@/api/auth";
import { showToast } from "@/components/common/Toast";
import { useLanguage } from "@/utils/language";

type SetupStep = "idle" | "scanning" | "backupCodes";

export const SecuritySettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState<SetupStep>("idle");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState("");
  const [accountUsername, setAccountUsername] = useState(user?.username || "");
  const [accountPassword, setAccountPassword] = useState("");
  const [passwordCurrent, setPasswordCurrent] = useState("");
  const [passwordNew, setPasswordNew] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [history, setHistory] = useState<LoginAttempt[]>([]);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await getLoginHistory());
    } catch {
      // non-critical, leave the list empty
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    setAccountUsername(user?.username || "");
  }, [user?.username]);

  const handleAccountUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateAccount(accountUsername, accountPassword);
      setAccountPassword("");
      showToast("Account updated", "success");
      window.location.reload();
    } catch (err) {
      showToast("Failed to update account", "error", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordNew !== passwordConfirm) {
      showToast("Passwords do not match", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      await updatePassword(passwordCurrent, passwordNew);
      setPasswordCurrent("");
      setPasswordNew("");
      setPasswordConfirm("");
      showToast("Password updated", "success");
    } catch (err) {
      showToast("Failed to update password", "error", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      const result = await setupTotp();
      setQrCode(result.qrCode);
      setSecret(result.secret);
      setStep("scanning");
    } catch (err) {
      showToast("Failed to start 2FA setup", "error", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await confirmTotp(confirmCode);
      setBackupCodes(result.backupCodes);
      setStep("backupCodes");
      setConfirmCode("");
    } catch (err) {
      showToast("Invalid code", "error", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinishSetup = () => {
    setStep("idle");
    setBackupCodes([]);
    setQrCode("");
    setSecret("");
    showToast("2FA enabled", "success");
    window.location.reload(); // refresh user info so totpEnabled reflects immediately
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await disableTotp(disablePassword);
      setShowDisableForm(false);
      setDisablePassword("");
      showToast("2FA disabled", "success");
      window.location.reload();
    } catch (err) {
      showToast("Failed to disable 2FA", "error", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldCheckIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          {t("security")}
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Two-factor authentication and login activity for this account
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("account")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 [&>form+form]:border-t [&>form+form]:border-gray-200 [&>form+form]:pt-5 dark:[&>form+form]:border-white/10">
          <form onSubmit={handleAccountUpdate} className="space-y-3">
            <div>
              <Label htmlFor="account-username">{t("username")}</Label>
              <Input
                id="account-username"
                required
                value={accountUsername}
                onChange={(e) => setAccountUsername(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="account-password">{t("currentPassword")}</Label>
              <Input
                id="account-password"
                type="password"
                required
                value={accountPassword}
                onChange={(e) => setAccountPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {t("saveUsername")}
            </Button>
          </form>

          <form onSubmit={handlePasswordUpdate} className="space-y-3">
            <div>
              <Label htmlFor="password-current">{t("currentPassword")}</Label>
              <Input
                id="password-current"
                type="password"
                required
                value={passwordCurrent}
                onChange={(e) => setPasswordCurrent(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password-new">{t("newPassword")}</Label>
              <Input
                id="password-new"
                type="password"
                required
                value={passwordNew}
                onChange={(e) => setPasswordNew(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password-confirm">{t("confirmNewPassword")}</Label>
              <Input
                id="password-confirm"
                type="password"
                required
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {t("changePassword")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockClosedIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            {t("twoFactorAuth")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === "idle" && (
            <>
              {user?.totpEnabled ? (
                <div className="space-y-3">
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                    2FA is enabled on this account.
                  </p>
                  {!showDisableForm ? (
                    <Button variant="secondary" onClick={() => setShowDisableForm(true)}>
                      Disable 2FA
                    </Button>
                  ) : (
                    <form onSubmit={handleDisable} className="space-y-3 max-w-sm">
                      <div>
                        <Label htmlFor="disable-password">Confirm your password</Label>
                        <Input
                          id="disable-password"
                          type="password"
                          required
                          value={disablePassword}
                          onChange={(e) => setDisablePassword(e.target.value)}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" variant="destructive" disabled={isSubmitting}>
                          Disable 2FA
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => setShowDisableForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                    Require a code from an authenticator app (in addition to your password) to
                    sign in. Recommended if this panel is reachable from the internet.
                  </p>
                  <Button onClick={handleStartSetup}>Enable 2FA</Button>
                </div>
              )}
            </>
          )}

          {step === "scanning" && (
            <form onSubmit={handleConfirm} className="space-y-4 max-w-sm">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Scan this QR code with your authenticator app (Google Authenticator, Authy,
                1Password, etc.), then enter the 6-digit code it shows to confirm.
              </p>
              {qrCode && (
                <img src={qrCode} alt="2FA QR code" className="rounded-lg border border-gray-200 dark:border-gray-800" />
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                Can't scan it? Enter this code manually: <span className="font-mono">{secret}</span>
              </p>
              <div>
                <Label htmlFor="confirm-code">6-digit code</Label>
                <Input
                  id="confirm-code"
                  inputMode="numeric"
                  required
                  autoFocus
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  className="text-center tracking-widest"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={isSubmitting}>
                  Confirm and enable
                </Button>
                <Button type="button" variant="secondary" onClick={() => setStep("idle")}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {step === "backupCodes" && (
            <div className="space-y-4 max-w-sm">
              <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                Save these backup codes now -- each works once, and this is the only time
                they're shown. Use one if you lose access to your authenticator app.
              </p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                {backupCodes.map((code) => (
                  <span key={code}>{code}</span>
                ))}
              </div>
              <Button onClick={handleFinishSetup}>I've saved these codes</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            {t("loginHistory")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">No login attempts recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">IP</th>
                    <th className="py-2 pr-4 font-medium">Method</th>
                    <th className="py-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((attempt) => (
                    <tr key={attempt.id} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {new Date(attempt.createdAt).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 font-mono">{attempt.ip}</td>
                      <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{attempt.method}</td>
                      <td className="py-2">
                        <span
                          className={
                            attempt.success
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-red-600 dark:text-red-400"
                          }
                        >
                          {attempt.success ? "Success" : "Failed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
